import { useState } from 'react';
import type { Grade, Resource, User } from '../lib/mockData';
import { ResourceCard } from './ResourceCard';
import { Plus, Upload } from 'lucide-react';
import ResourceUploadForm from './ResourceUploadForm';
import AssignmentCreateForm from './AssignmentCreateForm';
import AssignmentSubmissionForm from './AssignmentSubmissionForm';

interface GradeSectionProps {
  grade: Grade;
  onOpenResource: (resource: Resource) => void;
  currentUser: User;
  onResourceAdded: () => void;
}

export function GradeSection({ grade, onOpenResource, currentUser, onResourceAdded }: GradeSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [showAssignmentForm, setShowAssignmentForm] = useState(false);
  const [showSubmissionForm, setShowSubmissionForm] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<Resource | null>(null);

  const handleUploadResource = (resourceData: any) => {
    const newResource: Resource = {
      id: `${grade.id}-${resourceData.subject.toLowerCase()}-tb${Date.now()}`,
      title: resourceData.title,
      type: 'textbook',
      subject: resourceData.subject,
      description: resourceData.description,
      content: resourceData.content,
      uploadedAt: new Date().toISOString().split('T')[0],
      teacherId: currentUser.username,
      teacherName: currentUser.name
    };

    // Save to localStorage
    const savedResources = localStorage.getItem('userResources');
    const resources = savedResources ? JSON.parse(savedResources) : [];
    resources.push(newResource);
    localStorage.setItem('userResources', JSON.stringify(resources));

    setShowUploadForm(false);
    onResourceAdded();
  };

  const handleCreateAssignment = (assignmentData: any) => {
    const newAssignment: Resource = {
      id: `${grade.id}-${assignmentData.subject.toLowerCase()}-as${Date.now()}`,
      title: assignmentData.title,
      type: 'assignment',
      subject: assignmentData.subject,
      description: assignmentData.description,
      content: assignmentData.content + `\n\n**Due Date:** ${assignmentData.dueDate}`,
      uploadedAt: new Date().toISOString().split('T')[0],
      teacherId: currentUser.username,
      teacherName: currentUser.name
    };

    // Save to localStorage
    const savedResources = localStorage.getItem('userResources');
    const resources = savedResources ? JSON.parse(savedResources) : [];
    resources.push(newAssignment);
    localStorage.setItem('userResources', JSON.stringify(resources));

    setShowAssignmentForm(false);
    onResourceAdded();
  };

  const handleSubmitAssignment = (assignment: Resource) => {
    setSelectedAssignment(assignment);
    setShowSubmissionForm(true);
  };

  const handleSubmissionSuccess = () => {
    setShowSubmissionForm(false);
    setSelectedAssignment(null);
    alert('Assignment submitted successfully! Your teacher will review it soon.');
  };

  // Get user-created resources from localStorage
  const getUserResources = (): Resource[] => {
    const savedResources = localStorage.getItem('userResources');
    if (!savedResources) return [];
    const allResources = JSON.parse(savedResources);
    return allResources.filter((r: Resource) => r.id.startsWith(grade.id));
  };

  const allResources = [...grade.resources, ...getUserResources()];

  return (
    <>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700 transition-colors duration-300">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full bg-gradient-to-r from-green-600 to-green-700 dark:from-green-700 dark:to-green-800 text-white px-6 py-4 flex items-center justify-between hover:from-green-700 hover:to-green-800 dark:hover:from-green-600 dark:hover:to-green-700 transition-all duration-200"
        >
          <div className="flex items-center space-x-4">
            <div className="bg-white dark:bg-gray-700 rounded-full w-12 h-12 flex items-center justify-center">
              <span className="text-green-700 dark:text-green-400 font-bold text-xl">{grade.name}</span>
            </div>
            <div className="text-left">
              <h3 className="text-xl font-bold">{grade.displayName}</h3>
              <p className="text-sm text-green-100 dark:text-gray-300">{grade.description}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <span className="bg-white dark:bg-gray-700 text-green-700 dark:text-green-400 px-3 py-1 rounded-full text-sm font-semibold">
              {allResources.length} Resources
            </span>
            <svg
              className={`w-6 h-6 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </button>

        {isExpanded && (
          <div className="p-6 bg-gray-50 dark:bg-gray-900">
            {/* Teacher Actions */}
            {currentUser.role === 'teacher' && (
              <div className="mb-6 flex flex-wrap gap-3">
                <button
                  onClick={() => setShowUploadForm(true)}
                  className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors font-medium"
                >
                  <Upload className="w-4 h-4" />
                  Upload Textbook
                </button>
                <button
                  onClick={() => setShowAssignmentForm(true)}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors font-medium"
                >
                  <Plus className="w-4 h-4" />
                  Create Assignment
                </button>
              </div>
            )}

            {/* Resources Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {allResources.map((resource) => (
                <ResourceCard
                  key={resource.id}
                  resource={resource}
                  onOpen={onOpenResource}
                  isStudent={currentUser.role === 'student'}
                  onSubmitAssignment={handleSubmitAssignment}
                />
              ))}
            </div>

            {allResources.length === 0 && (
              <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                No resources available yet. {currentUser.role === 'teacher' ? 'Upload some content to get started!' : ''}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {showUploadForm && (
        <ResourceUploadForm
          onClose={() => setShowUploadForm(false)}
          onSubmit={handleUploadResource}
          currentUser={currentUser}
          grade={grade.displayName}
        />
      )}

      {showAssignmentForm && (
        <AssignmentCreateForm
          onClose={() => setShowAssignmentForm(false)}
          onSubmit={handleCreateAssignment}
          currentUser={currentUser}
          grade={grade.displayName}
        />
      )}

      {showSubmissionForm && selectedAssignment && (
        <AssignmentSubmissionForm
          assignment={selectedAssignment}
          onClose={() => {
            setShowSubmissionForm(false);
            setSelectedAssignment(null);
          }}
          onSuccess={handleSubmissionSuccess}
          currentUser={currentUser}
        />
      )}
    </>
  );
}
